import React from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningIcon from '@mui/icons-material/Warning'
import InfoIcon from '@mui/icons-material/Info'
import { useSnackbar } from 'notistack'
import moment from 'moment'
import { usePostSalesPayout } from '../../../api/sales-commission-incentive'
import { getErrorMessage } from '../../../api/utils'

const GeneratePayout = ({ open, onClose, monthYear, refetch }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { mutateAsync: generatePayouts, isLoading: isGenerating } = usePostSalesPayout()

  const handleGenerate = async () => {
    try {
      await generatePayouts({ monthYear })
      enqueueSnackbar('Payout generation process started in the background.', {
        variant: 'success'
      })
      refetch()
      onClose()
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' })
    }
  }

  const formattedMonthYear = moment(monthYear, 'MMYYYY').format('MMMM YYYY')

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm Payout Generation</DialogTitle>
      <DialogContent>
        <DialogContentText component='div'>
          <Typography variant='body1' gutterBottom>
            Are you sure you want to generate payout for <strong>{formattedMonthYear}</strong>?
          </Typography>
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography
              color='text.secondary'
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
            >
              <InfoIcon fontSize='small' color='primary' />
              Before generating payouts for {formattedMonthYear}, make sure you have:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleOutlineIcon fontSize='small' color='success' />
                </ListItemIcon>
                <ListItemText primary='Update Recipient Details' />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleOutlineIcon fontSize='small' color='success' />
                </ListItemIcon>
                <ListItemText primary='Upload all Commissions' />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleOutlineIcon fontSize='small' color='success' />
                </ListItemIcon>
                <ListItemText primary='Upload all Incentives' />
              </ListItem>
            </List>
          </Box>
          <Alert
            icon={<WarningIcon fontSize='inherit' />}
            severity='warning'
            sx={{ borderRadius: 2, fontSize: '0.875rem' }}
          >
            Existing recipients for this period will be <strong>skipped</strong> to prevent
            duplicates.
          </Alert>
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ p: 2, px: 3 }}>
        <Button onClick={onClose} disabled={isGenerating}>
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          color='primary'
          variant='contained'
          disabled={isGenerating}
          sx={{ borderRadius: 2, px: 3 }}
        >
          {isGenerating ? 'GENERATING...' : 'CONFIRM & GENERATE'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default GeneratePayout
